export enum PostHogEventEnum {
  Pageview = '$pageview',
  AiPreviewNewsClose = 'ai_preview_news_close',
  // App UI language picked in the profile; carries `language` and `previous_language`.
  UiLanguageChanged = 'ui_language_changed',

  // Auth
  LoginCompleted = 'login_completed',
  SignupCompleted = 'signup_completed',
  OAuthCompleted = 'oauth_completed',
  RegisterAttempt = 'register_attempt',
  RegisterResult = 'register_result',
  RegisterCaptchaFallbackShown = 'register_captcha_fallback_shown',
  // Fired on the social-login callback when an MCP-authorize `next` was staged for replay.
  OauthNextReplayed = 'oauth_next_replayed',
  // Bracket /auth/callback, which otherwise leaves no trace: `viewed` on mount,
  // `outcome` on every exit path with the time spent on the spinner.
  AuthCallbackViewed = 'auth_callback_viewed',
  AuthCallbackOutcome = 'auth_callback_outcome',
  // Email-activation resume. next_present=the link carried a `next`; next_accepted=getSafeNextPath
  // took it; next_replayed=the navigation actually ran. Fires at boot with replayed=false and
  // again from the navigation, so accepted-without-replayed isolates a drop after validation.
  ActivationNextReplayed = 'activation_next_replayed',

  // Invitation acceptance flow.
  InvitationLandingViewed = 'invitation_landing_viewed',
  InvitationAccepted = 'invitation_accepted',
  InvitationIgnored = 'invitation_ignored',
  InvitationDeclined = 'invitation_declined',

  // Knowledge Base
  ArticleCreated = 'article_created',
  ArticleUpdated = 'article_updated',
  ArticleDeleted = 'article_deleted',
  ArticleUploadStarted = 'article_upload_started',
  ArticleUploadCompleted = 'article_upload_completed',
  ArticleUploadFailed = 'article_upload_failed',
  // {article_id, status_code: number|null, retryable,
  //  reason: not_found|session_expired|auth_refresh_failed|forbidden|bad_request|backend_error|
  //  network_error} — kept in step with ArticleLoadFailureReason in articleLoadFailure.ts
  ArticleLoadFailed = 'article_load_failed',
  // {category, url_host, upload_type}: the Knowledge Base import twin of GettingStartedKnownBadUrlWarningShown
  KnowledgeBaseKnownBadUrlWarningShown = 'knowledge_base_known_bad_url_warning_shown',

  // Retrain
  RetrainStarted = 'retrain_started',
  RetrainCompleted = 'retrain_completed',
  RetrainFailed = 'retrain_failed',

  // Integrations
  IntegrationSetupStarted = 'integration_setup_started',
  MessengerConnectModalOpened = 'messenger_connect_modal_opened',
  // Fired the instant a user clicks "Sign in with Facebook Business" inside
  // the Messenger modal, BEFORE window.open. Pairs with MetaIntegrationCallback
  // (fired on popup return) to expose the popup-blocker / popup-never-opened
  // bucket as `messenger_oauth_started` events with no matching callback.
  // Emitted from `Domain/components/Integration/Messenger/Messenger.sheet.tsx`.
  MessengerOauthStarted = 'messenger_oauth_started',
  // Fired once per staged set per sheet opening, when the picker actually renders, so an
  // abandoned pick is distinguishable from one the merchant never saw. Reopening the sheet
  // re-fires, so count distinct sessions rather than raw events for unique pickers.
  MessengerPageSelectionShown = 'messenger_page_selection_shown',
  // Merchant left the sheet with a connect still in flight; carries { waited_ms }. Deliberately
  // NOT an integration_connect_failed reason — abandonment is the started->succeeded drop, and a
  // late Meta callback can still land a succeeded for the same attempt afterwards.
  // Pages staging in the same commit as the close can also emit this, so read the short
  // waited_ms tail as "closed around the callback", not as pure walk-away.
  MessengerConnectAbandoned = 'messenger_connect_abandoned',
  InstagramConnectModalOpened = 'instagram_connect_modal_opened',
  // Same trick as MessengerOauthStarted: fired before window.open, so a
  // started event with no matching MetaIntegrationCallback is the
  // popup-blocked bucket. Emitted from Integration/Instagram/Instagram.sheet.tsx.
  InstagramOauthStarted = 'instagram_oauth_started',
  InstagramDisconnected = 'instagram_disconnected',

  // Integrations — Slack v2 funnel
  // modal_opened: user clicked the Slack tile on the Integrations page.
  //   - integration_type: 'slack' | 'slackv2'
  // authenticate_clicked: user clicked "Authenticate with Slack" inside the
  //   sheet (fired from Domain/components/Integration/SlackV2/SlackV2.sheet.tsx).
  // oauth_callback: user returned to /channels after the Slack consent
  //   screen. Properties:
  //     - status: 'connected' | 'failed'
  //     - reason: backend-emitted reason on failure
  //       ('token_exchange' | 'team_already_connected' | 'save' |
  //        'servers_update') or null on success
  SlackIntegrationModalOpened = 'slack_integration_modal_opened',
  SlackIntegrationAuthenticateClicked = 'slack_integration_authenticate_clicked',
  SlackIntegrationOAuthCallback = 'slack_integration_oauth_callback',

  // Integrations — Meta OAuth popup callback (messenger / instagram)
  // Fired when MetaCallbackListener receives a `<integration>_connection_result`
  // postMessage from the OAuth popup. Properties:
  //   - integration_type: 'messenger' | 'instagram'
  //   - status: 'success' | 'error'
  //   - reason_code: backend-emitted code (e.g. 'connected',
  //     'page_granted_but_not_resolvable', 'webhook_subscribe_failed')
  MetaIntegrationCallback = 'meta_integration_callback',

  // WhatsApp OAuth funnel events carry launch mode, classified outcome, resolution, and dwell time.
  WhatsappIntegrationSheetOpened = 'whatsapp_integration_sheet_opened',
  WhatsappSignupInitiated = 'whatsapp_signup_initiated',
  WhatsappSignupObservation = 'whatsapp_signup_observation',
  WhatsappSignupPopupReturned = 'whatsapp_signup_popup_returned',
  WhatsappConnectWaitingHelpShown = 'whatsapp_connect_waiting_help_shown',
  WhatsappConnectRecoveryAction = 'whatsapp_connect_recovery_action',
  // Settles WhatsApp connect and reconnect attempts, including cancellation and timeouts.
  WhatsappSignupResult = 'whatsapp_signup_result',
  // Fires once when the integration first reaches connected; prior_status shows which state the connect recovered from.
  WhatsappConnectCompleted = 'whatsapp_connect_completed',
  // Post-connect lifecycle: the whole integration was disconnected, and a single number was enabled/disabled (is_active).
  WhatsappDisconnected = 'whatsapp_disconnected',
  WhatsappPhoneNumberToggled = 'whatsapp_phone_number_toggled',
  // Once per sheet open on a connected integration: display_name_state, is_test_number, has_inbound.
  WhatsappConnectedHealthShown = 'whatsapp_connected_health_shown',

  // Widget
  WidgetAppearanceChanged = 'widget_appearance_changed',
  // Copied a widget/embed/Shopify install snippet on Website → Install; see `snippet_type`.
  WidgetSnippetCopied = 'widget_snippet_copied',
  // Opened a platform install method on Website → Install; see `method` (wordpress/shopify/others/chat_page).
  InstallMethodOpened = 'install_method_opened',
  // Copied the Agent ID on Website → Install.
  InstallAgentIdCopied = 'install_agent_id_copied',
  // Opened a prefilled email to hand install off to a developer on Website → Install.
  InstallInstructionsEmailed = 'install_instructions_emailed',
  // Clicked "Ask AI for help" on Website → Install, opening the in-app Support AI.
  InstallAskAiClicked = 'install_ask_ai_clicked',
  // AI Preview — clicked "Enable Widget in [Shopify] Theme Editor"; opens the theme editor
  // in a new tab (window.open causes no DOM mutation, so without this it reads as a dead-click). source: 'ai_preview'.
  ShopifyThemeEditorOpened = 'shopify_theme_editor_opened',
  // A message was sent in the in-app AI Preview widget (the owner testing THEIR agent). Fired by
  // NewWidget on the widget's quickchat_message_sent postMessage; carries { author_type }.
  PreviewMessageSent = 'preview_message_sent',
  // The widget's post-send fallback recovered an AI reply the socket had dropped (the blank-pane bug).
  // Fired by NewWidget on the widget's quickchat_missed_reply_recovered postMessage; carries { socket_connected }.
  PreviewMissedReplyRecovered = 'preview_missed_reply_recovered',
  // An AI Preview message was really aimed at Quickchat Support ("how do i add you to my discord server").
  // Fires on every match, opened or not, so it is the per-rule precision denominator; never carries the text.
  PreviewSupportIntentDetected = 'preview_support_intent_detected',
  // A trigger opened the in-app Support AI on the user's behalf; carries { trigger } plus any
  // trigger-specific property (the AI Preview trigger adds `rule`). Shared by every auto-open trigger.
  SupportAiAutoOpened = 'support_ai_auto_opened',
  // A trigger fired but a guard held it back; carries { trigger, reason }.
  SupportAiAutoOpenSuppressed = 'support_ai_auto_open_suppressed',
  // The user closed a panel a trigger had auto-opened. The annoyance rate over support_ai_auto_opened.
  SupportAiAutoOpenDismissed = 'support_ai_auto_open_dismissed',

  // Actions
  ActionCreated = 'action_created',
  ActionTested = 'action_tested',
  // Activation-prompt funnel: a valid Action is built but still inactive (so it's
  // never bound to the agent). shown/enabled carry `surface: 'sheet_banner' | 'save_toast'`.
  ActionEnablePromptShown = 'action_enable_prompt_shown',
  ActionEnabledFromPrompt = 'action_enabled_from_prompt',
  // The sidebar flag on Actions & MCPs appeared; carries { attention_count }.
  AiActionsAttentionSidebarViewed = 'ai_actions_attention_sidebar_viewed',

  // Conversations
  ConversationResolved = 'conversation_resolved',
  ConversationReopened = 'conversation_reopened',
  HumanTakeoverInitiated = 'human_takeover_initiated',

  // Getting Started onboarding (new flow — replaces the removed 8-step checklist)
  GettingStartedViewed = 'getting_started_viewed',
  GettingStartedModeSelectionAbandoned = 'getting_started_mode_selection_abandoned',
  GettingStartedModeSelected = 'getting_started_mode_selected',
  GettingStartedCallBooked = 'getting_started_call_booked',
  GettingStartedAutomaticUrlValidated = 'getting_started_automatic_url_validated',
  GettingStartedAutomaticSubmitted = 'getting_started_automatic_submitted',
  GettingStartedAutomaticCompleted = 'getting_started_automatic_completed',
  GettingStartedAutomaticFailed = 'getting_started_automatic_failed',
  GettingStartedAutomaticLowQuality = 'getting_started_automatic_low_quality',
  GettingStartedAutomaticWrongSiteResolved = 'getting_started_automatic_wrong_site_resolved',
  GettingStartedQuickchatDomainWarningShown = 'getting_started_quickchat_domain_warning_shown',
  GettingStartedQuickchatDomainWarningResolved = 'getting_started_quickchat_domain_warning_resolved',
  GettingStartedPlaceholderDomainWarningShown = 'getting_started_placeholder_domain_warning_shown',
  GettingStartedKnownBadUrlWarningShown = 'getting_started_known_bad_url_warning_shown',
  GettingStartedKnownBadUrlWarningResolved = 'getting_started_known_bad_url_warning_resolved',
  // Client-side DoH pre-check found the typed domain does not exist (NXDOMAIN).
  // Resolved.outcome: 'corrected' | 'proceeded' | 'switched_to_manual'.
  GettingStartedDomainUnreachableWarningShown = 'getting_started_domain_unreachable_warning_shown',
  GettingStartedDomainUnreachableWarningResolved = 'getting_started_domain_unreachable_warning_resolved',
  // Language correction from the automatic summary; carries `language_count`.
  GettingStartedAutomaticLanguageChanged = 'getting_started_automatic_language_changed',
  // A host onboarding cannot scrape but we can reach another way (`saasHostRouter`) offered the
  // user a deferred remedy. `Shown` is the impression, `Added` the click that writes the
  // destination into the onboarding selection. Redemption is the matching
  // `next-steps-*-cta` click on AI Preview, so Shown → Added → connect is the funnel.
  // Both carry `source` ('known_bad_url' | 'scrape_error'), `kind`, `open_id` and `url_host`.
  GettingStartedSaasRemedyShown = 'getting_started_saas_remedy_shown',
  GettingStartedSaasRemedyAdded = 'getting_started_saas_remedy_added',
  GettingStartedAutomaticPostStepCompleted = 'getting_started_automatic_post_step_completed',
  GettingStartedDescriptionValidated = 'getting_started_description_validated',
  GettingStartedDescriptionSubmitted = 'getting_started_description_submitted',
  GettingStartedDescriptionClamped = 'getting_started_description_clamped',
  GettingStartedStepCompleted = 'getting_started_step_completed',
  GettingStartedStepSkipped = 'getting_started_step_skipped',
  // Discord-oriented onboarding: user clicked the "Add to your Discord server" CTA.
  // `source`: 'first_page' | 'deployment_step'. Funnel: this → DiscordIntegrationAuthorizeOpened → ≥1 Discord conversation.
  GettingStartedDiscordCtaClicked = 'getting_started_discord_cta_clicked',
  // The Deployment step's "Set up Telegram" button, shown once Telegram is picked; opens the Telegram sheet in place.
  GettingStartedTelegramCtaClicked = 'getting_started_telegram_cta_clicked',
  GettingStartedDiscordDescriptionValidated = 'getting_started_discord_description_validated',
  GettingStartedDiscordDescriptionSubmitted = 'getting_started_discord_description_submitted',
  // A paste over the character limit was trimmed to fit rather than rejected.
  // `action`: 'clamped' | 'undone' — the undo rate says whether the trim was welcome.
  GettingStartedDiscordDescriptionClamped = 'getting_started_discord_description_clamped',
  GettingStartedFinished = 'getting_started_finished',
  ShopifyReviewPromptShown = 'shopify_review_prompt_shown',
  ShopifyReviewPromptClicked = 'shopify_review_prompt_clicked',
  ShopifyReviewPromptDismissed = 'shopify_review_prompt_dismissed',
  // Onboarding exit / abandon funnel (the leave-onboarding X button)
  GettingStartedLeaveOpened = 'getting_started_leave_opened',
  GettingStartedLeftViaDelete = 'getting_started_left_via_delete',
  GettingStartedLeftViaLogout = 'getting_started_left_via_logout',
  // X on the last (pricing) step finishes setup directly; no dialog, so no leave_opened before it.
  GettingStartedLeftViaFinish = 'getting_started_left_via_finish',
  GettingStartedLeaveCanceled = 'getting_started_leave_canceled',
  // Onboarding pricing-step discount funnel. All three carry `trigger`: GettingStartedDiscountTrigger.
  GettingStartedDiscountOffered = 'getting_started_discount_offered',
  GettingStartedDiscountClaimed = 'getting_started_discount_claimed',
  GettingStartedDiscountUseLater = 'getting_started_discount_use_later',

  // New AI Agent creation — clicked an "Add new AI Agent" button. Both entry points
  // hard-nav to /add_new_ai/ (which creates immediately), so `source` is the only way
  // to tell them apart. source: AgentCreateSource.
  AgentCreateClicked = 'agent_create_clicked',

  // Knowledge Base — plan-limit upsell funnel
  KbLimitBannerViewed = 'kb_limit_banner_viewed',
  KbLimitWarningSidebarViewed = 'kb_limit_warning_sidebar_viewed',
  KbLimitModalOpened = 'kb_limit_modal_opened',
  KbFullPillViewed = 'kb_full_pill_viewed',
  KbUpgradeCtaClicked = 'kb_upgrade_cta_clicked',
  CreditTopupPanelViewed = 'credit_topup_panel_viewed',
  CreditTopupCheckoutStarted = 'credit_topup_checkout_started',
  CreditTopupCheckoutCreationFailed = 'credit_topup_checkout_creation_failed',
  CreditTopupCheckoutCancelled = 'credit_topup_checkout_cancelled',
  CreditTopupPurchaseConfirmed = 'credit_topup_purchase_confirmed',
  CreditTopupCardSetupStarted = 'credit_topup_card_setup_started',
  CreditTopupCardSetupCreationFailed = 'credit_topup_card_setup_creation_failed',
  CreditTopupCardSetupConfirmed = 'credit_topup_card_setup_confirmed',
  CreditTopupCardSetupCancelled = 'credit_topup_card_setup_cancelled',
  CreditTopupAutoReloadToggled = 'credit_topup_auto_reload_toggled',

  // Simulation Playground
  SimulationDatasetCreated = 'simulation_dataset_created',
  SimulationDatasetCsvUploaded = 'simulation_dataset_csv_uploaded',
  SimulationRunStarted = 'simulation_run_started',
  SimulationRunStartedOnStaleKb = 'simulation_run_started_on_stale_kb',
  SimulationRunCompleted = 'simulation_run_completed',
  SimulationRunOpened = 'simulation_run_opened',

  // Subscription — the funnel between an Upgrade CTA and Stripe. All four carry `entrypoint`
  // or `source` (the CTA that sent the user to Billing; `direct` when the URL wasn't tagged).
  // The plan grid rendered — i.e. the upgrade click actually landed on a usable Billing tab.
  BillingPlansViewed = 'billing_plans_viewed',
  BillingLowerPlansToggled = 'billing_lower_plans_toggled',
  // Switched monthly ↔ annual. Radix only fires on a real change, so re-clicks aren't counted.
  SubscriptionIntervalToggled = 'subscription_interval_toggled',
  // Clicked a plan card CTA, or the body of an upgrade card, which forwards the click to its CTA.
  // `outcome` says which branch the click opened (checkout, the change-plan dialog, the downgrade
  // dialog, or contact-sales).
  SubscriptionPlanSelected = 'subscription_plan_selected',
  // `/payments/handle-subscription/` returned 4xx/5xx: the button stops spinning and nothing
  // happens. Previously the only funnel outcome that produced no event at all.
  SubscriptionCheckoutCreationFailed = 'subscription_checkout_creation_failed',
  SubscriptionCheckoutStarted = 'subscription_checkout_started',
  PlanUpgraded = 'plan_upgraded',
  // Opened the "What's new" changelog dialog from the topbar account menu (Bell). No navigation/pageview fires otherwise.
  WhatsNewOpened = 'whats_new_opened',
  // Clicked the "AI credits" link (topbar/sidebar credits card or the billing usage card) that deep-links to the AI-credits FAQ. source carries the surface.
  AiCreditsFaqLinkClicked = 'ai_credits_faq_link_clicked',
  // The out-of-credits banner under the topbar rendered. can_manage_billing separates owners who
  // can act on it from editor/support viewers who can only pass the message on; remedy is the
  // fix the banner offered ('none' for those viewers).
  AgentMutedBannerViewed = 'agent_muted_banner_viewed',
  // Clicked that banner's CTA through to the Billing tab. Only reachable when can_manage_billing.
  // remedy separates the Discord-only credit top-up from the plan upgrade everyone else needs.
  AgentMutedBannerCtaClicked = 'agent_muted_banner_cta_clicked',

  // In-app Cal.com "Book a demo" flow. Opened fires when the modal opens (`source` = entry point:
  // topbar_menu/ai_preview/onboarding/...); Booked fires once via a global bookingSuccessful listener (the conversion).
  DemoModalOpened = 'demo_modal_opened',
  DemoCallBooked = 'demo_call_booked',

  // Why AI Said That
  WhyAiSaidThatOpened = 'why_ai_said_that_opened',

  // Identity — a persona/tone/prompt/guidelines/creativity/profession/language setting was saved
  // (autosave). `fields` lists the changed keys, so tone selection etc. become measurable.
  IdentitySettingSaved = 'identity_setting_saved',

  // Theme switched via the in-app toggle. `theme` = light|dark|system. NB: the Discord-referral
  // dark default is applied in the pre-paint bootstrap (before PostHog loads), so it isn't captured here.
  ThemeChanged = 'theme_changed',

  // Insights triage — user-action funnel on the Unified AI Insights page.
  // filter_tab_clicked.tab: 'market_insights' | 'flagged' | 'all'
  // topic_chip_clicked.topic: the open_ended_topic string that became a filter
  // set_reviewed_toggled.reviewed: bool (direction of the toggle)
  // mark_all_reviewed_clicked.unread_before: the count visible to the user at click time
  InsightsFilterTabClicked = 'insights_filter_tab_clicked',
  InsightsTopicChipClicked = 'insights_topic_chip_clicked',
  InsightsTopicFilterCleared = 'insights_topic_filter_cleared',
  InsightsSetReviewedToggled = 'insights_set_reviewed_toggled',
  InsightsMarkAllReviewedClicked = 'insights_mark_all_reviewed_clicked',

  // Manage Alerts (Insights → Manage Alerts tab) — alert config CRUD funnel.
  // created/updated: {name_length, description_length}; active_toggled: {is_active}; updated/active_toggled/deleted: {alert_id}.
  AlertCreated = 'alert_created',
  AlertUpdated = 'alert_updated',
  AlertActiveToggled = 'alert_active_toggled',
  AlertDeleted = 'alert_deleted',

  // Insights tab strip denominator. {page_tab: InsightsPageTabsEnum | 'unknown', is_landing_tab: bool}
  InsightsTabSelected = 'insights_tab_selected',

  // AI-Assisted Revenue (Insights → AI-Assisted Revenue tab) — fired once per tab view,
  // covering both in-app tab switches and email deep-link arrivals (?tab=assistedRevenue).
  // {has_orders: bool, total_orders: number, via_email: bool, utm_source/utm_medium/utm_campaign/utm_content: string|null}
  AssistedRevenueTabViewed = 'assisted_revenue_tab_viewed',

  // Article Usage analytics. View: {has_data, article_count, requested_window_days, effective_window_days, is_window_clamped}; export: {article_count, requested_window_days, effective_window_days}; examples: {article_id}.
  KbSourcesTabViewed = 'kb_sources_tab_viewed',
  KbSourcesExportClicked = 'kb_sources_export_clicked',
  KbSourcesExamplesOpened = 'kb_sources_examples_opened',

  // Sitewide-scrape (Knowledge Base "All Pages") funnel: map kicked off, review
  // modal seen, then scrape / discard / over-limit upgrade.
  SiteScrapeMapStarted = 'site_scrape_map_started',
  SiteScrapeReviewOpened = 'site_scrape_review_opened',
  SiteScrapeSelectionChanged = 'site_scrape_selection_changed',
  SiteScrapeReviewClosed = 'site_scrape_review_closed',
  SiteScrapeSubmitted = 'site_scrape_submitted',
  SiteScrapeDiscarded = 'site_scrape_discarded',
  SiteScrapeUpgradeShown = 'site_scrape_upgrade_shown',
  SiteScrapeUpgradeClicked = 'site_scrape_upgrade_clicked',
  SiteScrapeSnoozed = 'site_scrape_snoozed',

  // Discord own-token funnel; connect_result sends {status, reason, action, resolution, state_matched_at_request}, token_validated sends {result, message_content_intent}, and scenario_id is attached automatically.
  DiscordIntegrationSheetOpened = 'discord_integration_sheet_opened',
  DiscordIntegrationBotToggled = 'discord_integration_bot_toggled',
  DiscordIntegrationTokenSaved = 'discord_integration_token_saved',
  DiscordIntegrationReplyModeChanged = 'discord_integration_reply_mode_changed',
  DiscordIntegrationCrossChannelToggled = 'discord_integration_cross_channel_toggled',
  DiscordIntegrationConnectResult = 'discord_integration_connect_result',
  DiscordIntegrationSaveFailed = 'discord_integration_save_failed',
  DiscordIntegrationConnectDialogOpened = 'discord_integration_connect_dialog_opened',
  DiscordIntegrationTokenValidated = 'discord_integration_token_validated',
  DiscordIntegrationInviteLinkClicked = 'discord_integration_invite_link_clicked',

  // Integrations — Discord one-click OAuth (canonical "Quickchat AI" app). The
  // terminal succeeded/failed are the unified connect-funnel events fired from
  // useIntegrationOAuthCallback on redirect return; these cover the in-sheet
  // actions the funnel can't see. In-sheet events come from Discord.sheet.tsx;
  // OAuth nickname results come from useIntegrationOAuthCallback.ts.
  // oneclick_sheet_opened: the one-click Discord sheet became visible (funnel entry before authorize_opened).
  // authorize_opened: user clicked "Add to your Discord server" (before the
  //   full-page redirect to Discord's consent screen).
  // server_move_clicked: user chose "Move it here" for a server another of their AI Agents holds
  //   (before the full-page redirect to Discord's consent screen).
  // server_removed: a connected server was removed (bot left the guild).
  // server_toggled: a server's enable switch flipped. Properties: { enabled: bool }.
  // nickname_saved: a server rename succeeded. Properties: { cleared: bool, source: "oauth" | "settings" }.
  // nickname_preserved: a pre-connect name was ignored because the server already had a saved name.
  // automated_messages_opened: settings opened for a connected server.
  // automated_messages_saved: save succeeded. Properties identify which event rules are enabled.
  // actions_link_clicked: user followed the Discord AI Actions pointer from the Advanced setup
  //   callout to the Discord template gallery on Actions & MCPs.
  DiscordIntegrationOneClickSheetOpened = 'discord_integration_oneclick_sheet_opened',
  DiscordIntegrationAuthorizeOpened = 'discord_integration_authorize_opened',
  DiscordIntegrationServerMoveClicked = 'discord_integration_server_move_clicked',
  DiscordIntegrationServerRemoved = 'discord_integration_server_removed',
  DiscordIntegrationServerToggled = 'discord_integration_server_toggled',
  DiscordIntegrationNicknameSaved = 'discord_integration_nickname_saved',
  DiscordIntegrationNicknamePreserved = 'discord_integration_nickname_preserved',
  DiscordIntegrationAutomatedMessagesOpened = 'discord_integration_automated_messages_opened',
  DiscordIntegrationAutomatedMessagesSaved = 'discord_integration_automated_messages_saved',
  DiscordIntegrationActionsLinkClicked = 'discord_integration_actions_link_clicked',

  // Integrations — Telegram funnel. All emitted from
  // Domain/components/Integration/Telegram/Telegram.sheet.tsx; scenario_id is
  // attached automatically by usePostHogAnalytics.
  // sheet_opened: the Telegram sheet became visible (tile click or deep link).
  // saved: the PATCH succeeded (the funnel's terminal success event).
  //   Properties:
  //     - bot_active: the bot's enabled state after the save
  //     - token_changed: a new token was set
  //     - token_removed: the stored token was cleared (also disables the bot)
  // save_failed: the PATCH itself errored. Properties: { reason }.
  TelegramIntegrationSheetOpened = 'telegram_integration_sheet_opened',
  TelegramIntegrationSaved = 'telegram_integration_saved',
  TelegramIntegrationSaveFailed = 'telegram_integration_save_failed',

  // Telegram one-click: chat_confirmed is the connect, not the link click (a bind lands PENDING).
  TelegramIntegrationConnectLinkOpened = 'telegram_integration_connect_link_opened',
  TelegramIntegrationChatConfirmed = 'telegram_integration_chat_confirmed',
  TelegramIntegrationChatToggled = 'telegram_integration_chat_toggled',
  TelegramIntegrationChatRemoved = 'telegram_integration_chat_removed',
  TelegramIntegrationDirectLinkToggled = 'telegram_integration_direct_link_toggled',
  TelegramIntegrationDirectLinkCopied = 'telegram_integration_direct_link_copied',

  // Integrations — Email channel funnel. "Connected" for Email only means an
  // inbound address was generated, which is the cheap step; the channel does
  // nothing until mail actually forwards to it AND a support inbox is saved AND
  // it is switched on. So the unified connect trio (integration_type: 'email')
  // covers address generation, and these cover the three steps after it — the
  // ones that decide whether the channel ever answers an email. Emitted from
  // Domain/components/Integration/Email/Email.sheet.tsx except sheet_opened
  // (UI/Integrations.tsx, which is where the two entry points are
  // distinguishable); scenario_id is attached automatically.
  // sheet_opened: the Email sheet became visible. Properties:
  //   - source: EmailSheetSource — 'tile' (browsed the Channels page) vs
  //     'deep_link' (any ?open=email arrival). 'deep_link' is not "announcement
  //     traffic" on its own: PostOnboardingChecklist already navigates there,
  //     so split an announcement out by campaign params / $current_url.
  // address_copied: the dedicated <token>@in.quickchat.ai address was copied —
  //   the last in-app step before the user leaves for their mail provider, so
  //   copied-without-forwarding is the "went to configure and gave up" bucket.
  //   Properties: { forwarding_active } to separate the setup copy from a
  //   re-copy on an integration that already works.
  // forwarding_detected: forwarding_active flipped false -> true while the sheet
  //   was open, i.e. the first genuinely forwarded email landed. This is the
  //   hard step and the expected drop-off; fires at most once per sheet session.
  //   A same-tab reopen still reports a flip that happened while the sheet was
  //   closed (nothing refreshes the cache, so the stale false seeds and the poll
  //   observes true); only a fresh page load seeds true and stays silent, which
  //   is what makes this a lower bound rather than the true conversion count.
  //   Properties: { has_support_inbox, bot_active } — what was still missing at
  //   the moment forwarding started working.
  // support_inbox_saved / _save_failed: the required Reply-To inbox. Properties:
  //   - is_first_save: no support inbox was stored before (activation unblocked)
  //   - has_display_name: an optional sender name was saved alongside it
  //   - reason (failed only): bounded code, currently 'save_failed'.
  // active_toggled / _toggle_failed: the enable switch flipped. Properties:
  //   - enabled: the state the user asked for (the reached one on success)
  //   - forwarding_active (success only): enabling while forwarding has never
  //     been seen means the channel will sit silent, which reads as churn
  //   - reason (failed only): bounded code, currently 'save_failed'.
  // domain_verify_attempted: the optional branded-domain DNS check ran.
  //   Properties: { outcome: EmailDomainVerifyOutcome }.
  // removed: the integration was disconnected. Properties:
  //   { was_active, forwarding_active } — separates "never got it working" from
  //   "worked and rejected it".
  EmailIntegrationSheetOpened = 'email_integration_sheet_opened',
  EmailIntegrationAddressCopied = 'email_integration_address_copied',
  EmailIntegrationForwardingDetected = 'email_integration_forwarding_detected',
  EmailIntegrationSupportInboxSaved = 'email_integration_support_inbox_saved',
  EmailIntegrationSupportInboxCleared = 'email_integration_support_inbox_cleared',
  EmailIntegrationSupportInboxSaveFailed = 'email_integration_support_inbox_save_failed',
  EmailIntegrationActiveToggled = 'email_integration_active_toggled',
  EmailIntegrationActiveToggleFailed = 'email_integration_active_toggle_failed',
  EmailIntegrationDomainVerifyAttempted = 'email_integration_domain_verify_attempted',
  EmailIntegrationRemoved = 'email_integration_removed',

  // Inbox — Conversation Badge funnel. All emitted from
  // Domain/components/Inbox/ConversationBadgeSettings/ConversationBadgeSettings.dialog.tsx;
  // scenario_id is attached automatically by usePostHogAnalytics.
  // settings_opened: the badge settings dialog became visible (Inbox settings menu).
  // saved: the PATCH succeeded (the funnel's terminal success event).
  //   Properties:
  //     - badge_source: chosen source after save, or null when reset to the default (no Inbox badge)
  //     - previous_source: the source before this save, or null if it was the default
  //     - has_metadata_key: a metadata key was set (only meaningful when badge_source='metadata')
  // save_failed: the PATCH itself errored. Properties: { reason } — a bounded code
  //   ('backend_error' | 'network_error' | 'timeout' | 'unknown'); raw detail stays server-side.
  ConversationBadgeSettingsOpened = 'conversation_badge_settings_opened',
  ConversationBadgeSettingsSaved = 'conversation_badge_settings_saved',
  ConversationBadgeSettingsSaveFailed = 'conversation_badge_settings_save_failed',

  // Integrations — unified cross-channel connect funnel.
  // A consistent started/succeeded/failed trio that every integration sheet
  // emits, so one funnel works across ALL channels regardless of connect
  // mechanism (OAuth popup, token PATCH, app install). The detailed
  // per-integration events above (discord_integration_connect_result,
  // slack_integration_oauth_callback, meta_integration_callback,
  // whatsapp_connect_completed, ...) remain for channel-specific drill-downs.
  // All carry { integration_type: IntegrationConnectType }; scenario_id is
  // attached automatically by usePostHogAnalytics. Emitted via
  // Domain/hooks/Integration/integrationConnectAnalytics.ts (and the
  // useIntegrationConnectFunnel hook for in-sheet OAuth/token flows).
  //   started:   user initiated a connect/auth attempt. The reconnect rule
  //              depends on how a channel observes success, NOT on in-sheet vs
  //              OAuth: transition-based channels (HubSpot/Intercom/Zendesk —
  //              succeeded is inferred from a not-connected -> connected flip)
  //              emit it for a NEW connection only, because a reconnect stays
  //              connected and can't be observed; explicit-terminal channels
  //              (Discord/Telegram/Slack/Messenger — succeeded/failed come from
  //              a real result) emit it on every connect OR reconnect attempt.
  //   succeeded: the integration reached connected after a started attempt
  //              (fired once per connect).
  //   failed:    an explicit connect error. `reason` is a bounded code, e.g.
  //              'popup_blocked' | 'oauth_failed' | 'email_save_failed' |
  //              'missing_verify_token' | 'connect_failed' | 'save_failed' |
  //              'invalid_token' | 'invalid_token_pre_save' | 'intent_off_pre_save' |
  //              'missing_message_content_intent' | 'bot_error'
  //              | 'sdk_load_failed' | 'fetch_threw' | 'backend_error'.
  //              Keep reasons low-cardinality so the failed-by-reason breakdown
  //              stays usable; raw backend detail lives on the per-integration events.
  //              NB: a Discord poll timeout is intentionally NOT a `failed` reason
  //              (it's inconclusive, not an error — see connect_result above).
  //              WhatsApp cancellation, abandonment, and timeouts follow the same rule.
  // The started->succeeded drop is the abandonment rate; failed isolates hard errors.
  IntegrationConnectStarted = 'integration_connect_started',
  IntegrationConnectSucceeded = 'integration_connect_succeeded',
  IntegrationConnectFailed = 'integration_connect_failed',

  // Account MCP — "Manage your AI in ChatGPT / Claude" connect funnel. The
  // button fires no navigation/network call, so these are the ONLY frontend
  // usage signal; pair them with the backend OAuth events
  // (mcp_oauth_event_total / McpOAuthRefreshToken rows) to split "nobody clicks"
  // from "clicks but can't connect". All emitted from
  // Domain/components/Integration/AccountMcpConnect/AccountMcpConnect.sheet.tsx;
  // scenario_id is attached automatically by usePostHogAnalytics.
  // opened: the connect sheet became visible. Properties:
  //   - source: AccountMcpSource ('sidebar_pill' | 'identity_button' |
  //     'integrations_tile' | 'mcp_channel_sheet') or 'unknown' when opened
  //     without a source.
  // tab_selected: user switched the client tab. Properties: { client: AccountMcpClient }.
  // action_clicked: user took a connect action inside the sheet. Properties:
  //   - client: AccountMcpClient | 'endpoint' | 'docs'
  //   - action: 'copy_endpoint' | 'copy_command' | 'copy_config' | 'open_deeplink' |
  //     'open_docs' | 'open_app' | 'copy_prompt'
  //   - prompt_kind: 'setup' | 'review', only after a successful 'copy_prompt'.
  //   - source: AccountMcpActionSource, only on the logged-out entry-page
  //     variant, which fires { client: 'chatgpt' | 'claude_desktop', action: 'open_app' }
  //     from Domain/components/Auth/AiAppListingButton.tsx with no preceding `opened`.
  //     Filter it out when measuring the sheet.
  // The opened->action_clicked drop is the sheet-abandonment rate.
  AccountMcpOpened = 'account_mcp_opened',
  AccountMcpTabSelected = 'account_mcp_tab_selected',
  AccountMcpActionClicked = 'account_mcp_action_clicked',

  // Remote MCP AI action — connecting the agent TO an external MCP server (the
  // mirror image of the account-MCP events above, which expose US to a client).
  // Entry into this funnel is action_created { action_type: 'remote_mcp' }.
  // Pair with the backend counter remote_mcp_oauth_client_event_total to split a
  // frontend drop-off ("nobody finishes consent") from a server-side failure
  // ("consent finished but the token exchange 4xx'd").
  // `server` is the catalog entry name (e.g. 'Notion') or, for a manually entered
  // URL, its hostname only — never the full URL, which can carry a key in its
  // query string. scenario_id is attached automatically by usePostHogAnalytics.
  //
  // Connect probe (every server, OAuth or not):
  //   started:         a connect/reconnect attempt began. { server, entry_point:
  //                    RemoteMcpEntryPoint, is_reconnect }.
  //   succeeded:       the server returned its tools with no OAuth needed (open or
  //                    static-header server). { server, tool_count, is_reconnect }.
  //   requires_oauth:  the probe detected an OAuth challenge; the consent sub-funnel
  //                    below takes over. { server, provider_label }.
  //   failed:          the probe errored. { server, reason: RemoteMcpConnectFailReason }.
  //   awaiting_confirmation:
  //                    deep-link only. The probe found an unauthenticated server, so nothing was
  //                    created or activated and the user was handed the prefilled Connect step.
  //                    { server, tool_count }. Their click emits a fresh started -> succeeded pair.
  // requires_oauth is NOT a failure — started = succeeded + requires_oauth + failed +
  // awaiting_confirmation.
  //
  // OAuth consent sub-funnel (only servers that answered requires_oauth):
  //   oauth_intent:    the user clicked Connect, before the popup or any request.
  //   oauth_started:   the consent popup opened AND an authorize URL was obtained.
  //                    { provider_label, is_reconnect }.
  //   oauth_completed: tokens stored and post-auth finalization completed. { provider_label, tool_count,
  //                    duration_ms, settled_via: RemoteMcpOAuthSettledVia }.
  //                    settled_via is the health signal for the popup plumbing: a
  //                    drift toward 'status_poll' means postMessage/localStorage are
  //                    being severed (provider COOP) and durations will lengthen.
  //   oauth_failed:    { provider_label, reason: RemoteMcpOAuthFailReason, elapsed_ms }.
  //                    'cancelled', 'sheet_closed' and 'timed_out' are abandonment, not errors —
  //                    keep them separate from the hard failures when alerting.
  //   oauth_paused:    sheet closed after provider navigation; callback remains valid. { provider_label, elapsed_ms }.
  //   disconnected:    the user revoked an authorized server. { provider_label }.
  //
  // Catalog demand signals (which servers people want that we don't carry):
  //   catalog_search_empty:       a service-name search matched nothing. { query } (lowercased,
  //                               trimmed, capped, and discarded when URL/secret-like; once per
  //                               distinct safe query).
  //   catalog_suggestion_clicked: the "suggest an integration" mailto was clicked.
  RemoteMcpConnectStarted = 'remote_mcp_connect_started',
  RemoteMcpConnectSucceeded = 'remote_mcp_connect_succeeded',
  RemoteMcpConnectRequiresOauth = 'remote_mcp_connect_requires_oauth',
  RemoteMcpConnectFailed = 'remote_mcp_connect_failed',
  RemoteMcpConnectAwaitingConfirmation = 'remote_mcp_connect_awaiting_confirmation',
  // Captured before window.open; oauth_started keeps its post-navigation semantics.
  RemoteMcpOauthIntent = 'remote_mcp_oauth_intent',
  RemoteMcpOauthStarted = 'remote_mcp_oauth_started',
  RemoteMcpOauthCompleted = 'remote_mcp_oauth_completed',
  RemoteMcpOauthFailed = 'remote_mcp_oauth_failed',
  RemoteMcpOauthPaused = 'remote_mcp_oauth_paused',
  RemoteMcpDisconnected = 'remote_mcp_disconnected',
  RemoteMcpCatalogSearchEmpty = 'remote_mcp_catalog_search_empty',
  RemoteMcpCatalogSuggestionClicked = 'remote_mcp_catalog_suggestion_clicked',
}

// Entry point that opened the account-MCP connect sheet, for
// AccountMcpOpened.source. Kept next to the event enum so the funnel SQL
// contract is greppable from one place.
export type AccountMcpSource =
  | 'sidebar_pill'
  | 'identity_button'
  | 'integrations_tile'
  | 'mcp_channel_sheet';

// Logged-out page that fired AccountMcpActionClicked without opening the sheet,
// for its `source` property. Kept apart from AccountMcpSource so the latter
// stays the list of sheet entry points.
export type AccountMcpActionSource = 'login_page' | 'register_page';

// Client tab / target in the account-MCP connect sheet, for
// AccountMcpTabSelected.client and AccountMcpActionClicked.client.
export type AccountMcpClient =
  | 'chatgpt'
  | 'claude_desktop'
  | 'claude_code'
  | 'cursor'
  | 'codex'
  | 'whatsapp';

// Channel discriminator for the unified integration connect funnel events.
// Kept next to the event enum so the funnel SQL contract (which integration_type
// values an analyst can expect) is greppable from one place.
export type IntegrationConnectType =
  | 'hubspot'
  | 'intercom'
  | 'zendesk'
  | 'shopify'
  | 'discord'
  | 'telegram'
  | 'slack'
  | 'messenger'
  | 'instagram'
  | 'whatsapp'
  | 'drupal'
  | 'email';

// Observations and terminal outcomes for WhatsappSignupResult.status.
export type WhatsAppSignupStatus =
  | 'connected'
  | 'failed'
  | 'cancelled'
  | 'abandoned'
  | 'timed_out'
  | 'awaiting_selection';

// Channel that produced a WhatsappSignupResult observation or outcome, for its resolution.
export type WhatsAppSignupResolution =
  | 'popup_postmessage'
  | 'redirect_return'
  | 'status_poll'
  | 'status_timeout'
  | 'popup_closed'
  | 'popup_deadline'
  | 'popup_blocked'
  | 'waba_select'
  | 'recovery_dismissed'
  // A newer launch replaced the attempt this page was observing; nonterminal, so it can still connect.
  | 'superseded';

// Entry point that opened the Email sheet, for EmailIntegrationSheetOpened.source.
// 'deep_link' is every ?open=email arrival — docs, release notes and campaigns,
// but also the post-onboarding checklist, which is the only producer today.
export type EmailSheetSource = 'tile' | 'deep_link';

// Outcome of the optional branded-domain DNS check, for
// EmailIntegrationDomainVerifyAttempted.outcome. 'pending' = the request
// succeeded but DNS has not propagated yet; 'failed' = the request itself errored.
export type EmailDomainVerifyOutcome = 'authenticated' | 'pending' | 'failed';

// Property values for `GettingStartedAutomaticUrlValidated.outcome`. Kept here
// next to the event enum so the funnel SQL contract is greppable from one
// place when an analyst asks "what values can this take?".
export type GettingStartedAutomaticUrlValidatedOutcome =
  | 'valid'
  | 'empty'
  | 'has_whitespace'
  | 'invalid_format';

// How a visitor resolved the "that address takes us to <other site>" question.
export type GettingStartedWrongSiteOutcome =
  | 'used_landed_site'
  | 'changed_url'
  | 'switched_to_manual';

// source for AgentCreateClicked: which entry point launched /add_new_ai/.
// 'agent_switcher' = "Add new AI Agent" item inside the sidebar switcher dropdown;
// 'sidebar_add_button' = the "+" button next to the sidebar "AI Agent" group label.
export type AgentCreateSource = 'agent_switcher' | 'sidebar_add_button';

// How the user reached a Remote MCP connect attempt, for RemoteMcpConnectStarted.entry_point.
// 'catalog' = a category tile, 'catalog_popular' = a Popular-spotlight tile, 'catalog_search' = a
// tile in search results, 'manual' = a hand-typed URL, 'reconnect' = the Connect/Reconnect button
// on an action that already exists. Kept next to the event enum so the funnel SQL contract is
// greppable from one place.
export type RemoteMcpEntryPoint =
  | 'catalog'
  | 'catalog_popular'
  | 'catalog_search'
  | 'deep_link'
  | 'manual'
  | 'reconnect';

// Bounded failure codes for RemoteMcpConnectFailed.reason. Deliberately coarse: only the HTTP
// status is visible here, so the precise cause (unreachable / not-an-MCP-server / bad static
// credentials / SSRF-blocked) stays in the backend's user-facing error classification rather than
// being copied into analytics properties.
//   probe_rejected: the /connect probe 4xx'd — the server could not be used as given.
//   create_failed:  the probe succeeded but persisting the action 4xx'd; on this path that is
//                   almost always the plan's AI-action limit.
//   backend_error:  5xx or no response at all.
export type RemoteMcpConnectFailReason = 'probe_rejected' | 'create_failed' | 'backend_error';

// User abandonment is separate; create_failed prevents plan limits from looking like OAuth errors.
export type RemoteMcpOAuthFailReason =
  | 'popup_blocked'
  | 'create_failed'
  | 'start_failed'
  | 'callback_error'
  | 'timed_out'
  | 'cancelled'
  | 'sheet_closed'
  | 'finalize_failed';

// Which signal settled a successful consent, for RemoteMcpOauthCompleted.settled_via.
// 'post_message' = the callback page reached window.opener; 'local_storage' = COOP severed the
// opener so the same-origin storage write won; 'status_poll' = both were lost and only the backend
// status poll observed it. Drift toward 'status_poll' is the early warning that the popup
// plumbing is degrading.
export type RemoteMcpOAuthSettledVia = 'post_message' | 'local_storage' | 'status_poll';

// What opened the onboarding discount offer: the Skip button, the exit X, or the pointer leaving the page.
export type GettingStartedDiscountTrigger = 'skip' | 'x_button' | 'exit_intent';

export enum PostHogFeatureFlagEnum {
  AiPreviewNews = 'ai_preview_news',
  SignupBackgroundTest = 'signup_background_test',
  WhatsAppCoexistence = 'whatsapp_coexistence',
}

export enum SignupBackgroundVariant {
  Control = 'control',
  Blur = 'blur',
}

export enum PostHogUserPropertyEnum {
  AiPreviewNews = 'ai_preview_news',
}
